import { formatMoney } from './money-formatting.js';

const thermalParentProductId = '8972804456698';
const thermalParentVariantId = '47876852842746';

function readJson(element) {
  try {
    return JSON.parse(element.textContent);
  } catch (error) {
    return null;
  }
}

export function hasActiveHiddenSellingPlan(form) {
  return Array.from(form.querySelectorAll('[name="selling_plan"]')).some(
    (plan) => plan instanceof HTMLInputElement && plan.type === 'hidden' && Boolean(plan.value)
  );
}

export function sellingPlanStateSignature(form) {
  return Array.from(form.querySelectorAll('[name="selling_plan"]'))
    .map((plan) => [plan.tagName, plan.type || '', plan.checked ? '1' : '0', plan.value || ''].join(':'))
    .join('|');
}

export function sellingPlanSignatureChanged(previousSignature, nextSignature) {
  return previousSignature !== nextSignature;
}

export function discountedUnitPrice(price, percentage) {
  if (!Number.isSafeInteger(price) || price < 0 || !Number.isInteger(percentage) || percentage < 0 || percentage > 100) return null;
  // Native ordinary discounts floor the unit allocation in integer currency units.
  // Line allocations can differ;
  // never multiply this unit estimate to promise an exact order subtotal.
  const discountNumerator = price * percentage;
  if (!Number.isSafeInteger(discountNumerator)) return null;
  return price - Math.floor(discountNumerator / 100);
}

export function offerApplies(offer, variant, productHandle) {
  if (offer.enabled !== true) return false;
  if (offer.variant_id && String(offer.variant_id) !== String(variant.id)) return false;
  if (offer.sku && offer.sku !== variant.sku) return false;
  if (offer.variant_skus && !offer.variant_skus.includes(variant.sku)) return false;
  if (offer.product_handle && offer.product_handle !== productHandle) return false;
  if (offer.product_handles && !offer.product_handles.includes(productHandle)) return false;
  return true;
}

export function regularOfferModel(manifest, variant, productHandle, quantity, isSubscription) {
  const rows = (manifest?.offers || [])
    .filter((offer) => offerApplies(offer, variant, productHandle) && !offer.condition)
    .filter((offer) => Number.isSafeInteger(offer.minimum_quantity) && offer.minimum_quantity > 0 && discountedUnitPrice(variant.price, offer.percentage) !== null)
    .sort((a, b) => a.minimum_quantity - b.minimum_quantity)
    .map((offer, index, offers) => ({
      minimum: offer.minimum_quantity,
      maximum: offers[index + 1] ? offers[index + 1].minimum_quantity - 1 : null,
      percentage: offer.percentage,
      price: discountedUnitPrice(variant.price, offer.percentage),
    }));
  const selected = Number.isSafeInteger(quantity) && quantity > 0 && !isSubscription
    ? rows.find((row) => quantity >= row.minimum && (row.maximum === null || quantity <= row.maximum))
    : null;
  const ribbon = (manifest?.offers || []).find((offer) => offer.condition === 'qualifying-impact-paper' && offerApplies(offer, variant, productHandle));
  return {
    rows, selected,
    subscriptionPrice: discountedUnitPrice(variant.price, manifest?.subscription?.percentage),
    ribbonPrice: ribbon ? discountedUnitPrice(variant.price, ribbon.percentage) : null,
  };
}

export function owningOfferForm(root) {
  const nestedForm = root.closest('product-form-component')?.querySelector('form');
  if (nestedForm) return nestedForm;
  const formId = root.dataset.scProductFormId;
  if (!formId) return null;
  const form = document.getElementById(formId);
  const section = root.closest('.shopify-section');
  return section && form?.matches('form') && form.closest('.shopify-section') === section ? form : null;
}

function updateOffer(root, variantId) {
  const variantsNode = root.querySelector('[data-sc-native-variants]');
  const variantState = root.querySelector('[data-sc-variant-state]');
  if (!variantsNode || !variantState) return;

  const variants = readJson(variantsNode) || [];
  const variant = variants.find((candidate) => String(candidate.id) === String(variantId));
  if (!variant) return;

  const form = owningOfferForm(root);
  const manifestNode = root.querySelector('[data-sc-native-manifest]');
  const rowsNode = root.querySelector('[data-sc-offer-rows]');
  const formatNode = root.querySelector('[data-sc-money-format]');
  if (form && manifestNode && rowsNode && formatNode) {
    const quantity = Number(form.querySelector('[name="quantity"]')?.value);
    const isSubscription = hasActiveHiddenSellingPlan(form);
    const model = regularOfferModel(readJson(manifestNode), variant, root.dataset.productHandle, quantity, isSubscription);
    const money = (price) => formatMoney(price, formatNode.content?.textContent || formatNode.textContent, root.dataset.currency);
    const rows = model.rows.map((row) => {
      const element = document.createElement('tr');
      element.dataset.selected = String(row === model.selected);
      const range = row.maximum === null ? `${row.minimum}+` : `${row.minimum}–${row.maximum}`;
      for (const [index, value] of [range, `${row.percentage}%`, money(row.price)].entries()) {
        const cell = document.createElement(index === 0 ? 'th' : 'td');
        if (index === 0) cell.scope = 'row';
        cell.textContent = value;
        element.append(cell);
      }
      return element;
    });
    if (rows.length) rowsNode.replaceChildren(...rows);
    const subscription = root.querySelector('[data-sc-subscription-offer]');
    const subscriptionPrice = root.querySelector('[data-sc-subscription-price]');
    if (subscription && subscriptionPrice && model.subscriptionPrice !== null) {
      subscription.dataset.selected = String(isSubscription);
      subscriptionPrice.textContent = money(model.subscriptionPrice);
    }
    const ribbonPrice = root.querySelector('[data-sc-ribbon-price]');
    if (ribbonPrice && model.ribbonPrice !== null) ribbonPrice.textContent = money(model.ribbonPrice);
  }

  variantState.textContent = `${variant.sku || 'Selected variant'}${variant.available ? ' is available.' : ' is unavailable.'}${variant.media_id ? ' Product media follows the selected variant.' : ''}`;

  // This component does not add or remove selling_plan fields or cart properties.
  // Shopify Subscriptions owns the selected selling plan; Shopify native rules own price enforcement.
}

function thermalSafeVariantId(root, variantInput) {
  if (root.dataset.productId !== thermalParentProductId) return variantInput.value;
  if (variantInput.value !== thermalParentVariantId) variantInput.value = thermalParentVariantId;
  return thermalParentVariantId;
}

export function placeBuyerConsent(form) {
  const section = form.closest?.('.shopify-section') || form;
  const consent = section.querySelector('#shopify-buyer-consent');
  const fallback = form.querySelector('[data-sc-native-buyer-consent-fallback]');
  const addToCart = form.querySelector('add-to-cart-component') || form.querySelector('button[name="add"]');
  if (!addToCart) return;

  if (consent) {
    if (consent.nextElementSibling !== addToCart) addToCart.before(consent);
    if (fallback) fallback.hidden = true;
    return;
  }

  if (!fallback) return;
  const isSubscription = hasActiveHiddenSellingPlan(form);
  fallback.hidden = !isSubscription;
  if (isSubscription && fallback.nextElementSibling !== addToCart) addToCart.before(fallback);
}

function init(root) {
  const form = owningOfferForm(root);
  const variantInput = form?.querySelector('[name="id"]');
  if (!form || !variantInput) return;

  const controller = new AbortController();
  const section = root.closest('.shopify-section');
  section?.classList.add('sc-offer-product');
  form.classList.add('sc-offer-product');
  let disposed = false;
  let scheduled = false;
  let pendingVariantId = variantInput.value;
  let consentObserver;

  const cleanup = () => {
    if (disposed) return;
    disposed = true;
    controller.abort();
    consentObserver?.disconnect();
  };

  const refresh = () => {
    const variantId = thermalSafeVariantId(root, variantInput);
    pendingVariantId = variantId;
    updateOffer(root, variantId);
    placeBuyerConsent(form);
  };
  const scheduleRefresh = (event) => {
    if (disposed) return;
    pendingVariantId = thermalSafeVariantId(root, variantInput);
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      if (!root.isConnected) {
        cleanup();
        return;
      }
      refresh();
    });
  };
  section?.addEventListener('variant:update', scheduleRefresh, { signal: controller.signal });
  section?.addEventListener('quantity-selector:update', scheduleRefresh, { signal: controller.signal });
  section?.addEventListener('change', (event) => {
    const control = event.target;
    const isSubscriptionControl = (
      control instanceof HTMLInputElement && (
        control.matches('input[data-radio-type]') || (
          (control.id?.startsWith('tab_subscribe_') || control.id?.startsWith('tab_otp_')) &&
          control.closest('.shopify_subscriptions_app_block')?.closest('.shopify-section') === section
        )
      )
    ) || (
      control instanceof HTMLSelectElement && (
        control.matches('select.shopify_subscriptions_group_dropdown') || (
          control.matches('select.group_dropdown') &&
          control.closest('.shopify_subscriptions_app_block')?.closest('.shopify-section') === section
        )
      )
    );
    if (isSubscriptionControl) scheduleRefresh(event);
  }, { signal: controller.signal });
  form.addEventListener('change', scheduleRefresh, { signal: controller.signal });
  form.addEventListener('input', (event) => {
    if (event.target?.name === 'quantity') scheduleRefresh(event);
  }, { signal: controller.signal });

  placeBuyerConsent(form);
  consentObserver = new MutationObserver(() => placeBuyerConsent(form));
  consentObserver.observe(section || form, { childList: true, subtree: true });

  refresh();
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-sc-native-purchase-offer]').forEach(init);
}
